import { useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Field,
} from "@h2j-data-team/ui";
import { setBackupAlertDismissed } from "@/lib/use-backup-alert-preference";

interface BackupRecommendationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  isPending?: boolean;
  /** Where focus goes when the dialog closes (the button that opened it). */
  restoreFocusRef?: RefObject<HTMLElement | null>;
}

export function BackupRecommendationDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending = false,
  restoreFocusRef,
}: BackupRecommendationDialogProps) {
  const { t } = useTranslation("common");
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loading = isPending || isLoading;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      if (dontShowAgain) {
        setBackupAlertDismissed(true);
      }
      await onConfirm();
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setDontShowAgain(false);
    }
    onOpenChange(next);
  };

  // The library AlertDialog does not close on its own and, while busy, ignores
  // Cancel and Escape: the host closes it once the save has been handed over.
  return (
    <AlertDialog
      open={open}
      onOpenChange={handleOpenChange}
      busy={loading}
      restoreFocusRef={restoreFocusRef}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("backupDialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("backupDialog.description")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogBody>
          <Field inline label={t("backupDialog.dontShowAgain")}>
            <Checkbox
              checked={dontShowAgain}
              onCheckedChange={(checked) => setDontShowAgain(checked === true)}
              disabled={loading}
            />
          </Field>
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline">{t("cancel")}</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button loading={loading} onClick={() => void handleConfirm()}>
              {t("backupDialog.continue")}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
